// Synthesized read-only Monaco models backing the native peek widget.
//
// One model per referencing persisted object, keyed by the `jamsql-ref:` URI
// minted in `peekReferenceUri.ts`. Monaco standalone's
// `StandaloneTextModelService.createModelReference` resolves a peek location
// by looking the URI up in the model registry, so the model MUST exist before
// the `ReferenceProvider` returns its `Location[]`.
//
// Lifecycle:
//   • create — on the first peek that lands in the object.
//   • refresh — a later peek re-fetches the body; a changed body is pushed via
//     `setValue` so the whole document swaps atomically (no edit-shaped deltas
//     for undo stacks or listeners to chew on; editor-level readOnly guards
//     don't intercept model-level writes either way).
//   • dispose — when the owning connection goes away
//     (`navigationalLifecycle.tearDownConnection`) so models never outlive
//     their catalog or leak across reconnects.
//
// The Monaco namespace is injected (not imported) so unit tests can drive the
// cache with a tiny fake and the module stays free of runtime monaco imports.

import type * as monacoNs from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

import type { ObjectKind, QualifiedName } from '../types';

import { buildPeekReferenceUri, peekReferenceTargetFor } from './peekReferenceUri';

/** Structural subset of the Monaco namespace the cache needs. */
export type PeekModelMonacoApi = {
  readonly Uri: { parse: (value: string) => monacoNs.Uri };
  readonly editor: {
    createModel: (
      value: string,
      language?: string,
      uri?: monacoNs.Uri,
    ) => monacoNs.editor.ITextModel;
    getModel: (uri: monacoNs.Uri) => monacoNs.editor.ITextModel | null;
  };
};

/** Everything the editor-opener needs to route a peek "open" back into the
 *  workspace via `openReferenceLocation`. */
export type PeekReferenceModelEntry = {
  readonly uri: string;
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
  readonly model: monacoNs.editor.ITextModel;
  /** Last body pushed into the model — the refresh comparison key. */
  readonly bodyText: string;
};

export type EnsurePeekReferenceModelArgs = {
  readonly monaco: PeekModelMonacoApi;
  readonly languageId: string;
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
  readonly bodyText: string;
};

const byUri = new Map<string, PeekReferenceModelEntry>();
const byConnection = new Map<string, Set<string>>();

/** Create (or refresh) the synthesized model for one referencing object and
 *  return its cache entry. Safe to call on every peek — the second call for an
 *  unchanged body is a pure map lookup. */
export function ensurePeekReferenceModel(
  args: EnsurePeekReferenceModelArgs,
): PeekReferenceModelEntry {
  const uriObject = args.monaco.Uri.parse(
    buildPeekReferenceUri(
      peekReferenceTargetFor({
        connectionId: args.connectionId,
        database: args.database,
        objectKind: args.objectKind,
        objectName: args.objectName,
      }),
    ),
  );
  const uri = uriObject.toString();

  const cached = byUri.get(uri);
  if (cached && !cached.model.isDisposed()) {
    if (cached.bodyText === args.bodyText) return cached;
    cached.model.setValue(args.bodyText);
    return replaceEntry({ ...cached, bodyText: args.bodyText });
  }

  // A model can survive in Monaco's registry without a cache entry when the
  // renderer hot-reloaded this module; adopt it rather than colliding on
  // `createModel` (which throws on a duplicate URI).
  const existingModel = args.monaco.editor.getModel(uriObject);
  const model =
    existingModel && !existingModel.isDisposed()
      ? existingModel
      : args.monaco.editor.createModel(args.bodyText, args.languageId, uriObject);
  if (existingModel && !existingModel.isDisposed() && existingModel.getValue() !== args.bodyText) {
    existingModel.setValue(args.bodyText);
  }

  return replaceEntry({
    uri,
    connectionId: args.connectionId,
    database: args.database,
    engineId: args.engineId,
    objectKind: args.objectKind,
    objectName: args.objectName,
    model,
    bodyText: args.bodyText,
  });
}

/** Cache lookup by the `Uri.toString()` form Monaco hands to an editor
 *  opener. Returns `null` for URIs we never minted. */
export function lookupPeekReferenceModel(uri: string): PeekReferenceModelEntry | null {
  const entry = byUri.get(uri);
  if (!entry) return null;
  if (entry.model.isDisposed()) {
    forgetEntry(entry);
    return null;
  }
  return entry;
}

/** Dispose every synthesized model owned by a connection. Returns how many
 *  were disposed so the lifecycle can log / tests can assert. */
export function disposePeekReferenceModelsForConnection(connectionId: string): number {
  const uris = byConnection.get(connectionId);
  if (!uris) return 0;
  let disposed = 0;
  for (const uri of [...uris]) {
    const entry = byUri.get(uri);
    if (!entry) continue;
    if (!entry.model.isDisposed()) {
      entry.model.dispose();
      disposed += 1;
    }
    forgetEntry(entry);
  }
  byConnection.delete(connectionId);
  return disposed;
}

/** Test-only — drop every cached model without touching Monaco. */
export function __resetPeekReferenceModelsForTest(): void {
  for (const entry of [...byUri.values()]) {
    if (!entry.model.isDisposed()) entry.model.dispose();
  }
  byUri.clear();
  byConnection.clear();
}

/** Test-only — cached URIs for a connection. */
export function __peekReferenceModelUrisForTest(connectionId: string): readonly string[] {
  return [...(byConnection.get(connectionId) ?? [])];
}

function replaceEntry(entry: PeekReferenceModelEntry): PeekReferenceModelEntry {
  byUri.set(entry.uri, entry);
  let uris = byConnection.get(entry.connectionId);
  if (!uris) {
    uris = new Set<string>();
    byConnection.set(entry.connectionId, uris);
  }
  uris.add(entry.uri);
  return entry;
}

function forgetEntry(entry: PeekReferenceModelEntry): void {
  byUri.delete(entry.uri);
  byConnection.get(entry.connectionId)?.delete(entry.uri);
}
